import React, {PropsWithChildren, useState} from 'react';
import ChekinAPI, {
  AUTH_INIT_MODELS,
  type OldShortLink,
  RESERVATION_SEARCH_MODE,
  SDKApiKeyResponse,
  type ShortLink,
} from '@guestapp/sdk';
import i18n from 'i18next';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import ChekinProSettings from '../../ChekinProSettings';
import {LocalStorage} from '@guestapp/core';
import {useRedirectToUrlFromShortLink} from 'hooks/useRedirectToUrlFromShortLink';
import {environments} from 'configs';
import {
  MAIN_TOKEN_ID_KEY,
  QUERY_CACHE_KEYS,
  RESERVATION_ID_KEY,
  SHARE_LINK,
  SHORT_LINK_TYPE,
} from 'utils/constants';
import {
  getCustomFormVersion,
  getObjectDetails,
  getReservationFromStorage,
  getResourceType,
  getTemplateId,
  isNewShortLink,
  isShortLink,
} from './utils';

const IS_SDK_MODE = environments.VITE_APP_IS_SDK;

type ContextProps = ChekinAPI['reservation'] &
  ChekinAPI['guest'] &
  ChekinAPI['identityVerification'] &
  ChekinAPI['verification'] &
  ChekinAPI['customForm'] &
  ChekinAPI['validations'] &
  ChekinAPI['locations'] &
  ChekinAPI['guestFormAdditionalData'] &
  ChekinAPI['housing'] &
  ChekinAPI['guestGroup'] &
  ChekinAPI['documents'] &
  ChekinAPI['payments'] &
  ChekinAPI['websocket'] &
  ChekinAPI['upselling'] &
  ChekinAPI['feedback'] &
  ChekinAPI['propertyProtection'] &
  ChekinAPI['locks'] &
  ChekinAPI['theme'] &
  ChekinAPI['guidebooks'] &
  Omit<ChekinAPI['auth'], 'init' | 'initChekin'> &
  ChekinAPI['taxes'] & {
    isTokenValid: boolean;
    isAuthInitialization: boolean;
    isAuthInitializationError: boolean;
    authError?: string;
    isSuccessSDK: boolean;
    foundReservationId?: string;
    mainToken?: string | null;
    hasToken?: boolean;
    objectId?: string;
    modelName?: string;
    link?: string;
    redirectTo?: string;
    linkType?: SHORT_LINK_TYPE;
    templateId?: string | null;
    customFormsVersion?: 'V2' | 'V1';
    reservationSearchMode?: RESERVATION_SEARCH_MODE;
  };

const ChekinSDKContext = React.createContext<ContextProps>({} as ContextProps);

function ChekinSDKProvider({children}: PropsWithChildren) {
  const {t} = useTranslation();
  const [foundReservationId, setFoundReservationId] = useState<string | undefined>(
    getReservationFromStorage,
  );

  // chekin-sdk =-------------------------------------------------------------------
  const chekinProApiKey =
    ChekinProSettings.apiKey || import.meta.env.VITE_APP_TEST_API_KEY || '';
  const defaultLanguage = ChekinProSettings.defaultLanguage;

  React.useEffect(() => {
    if (IS_SDK_MODE) {
      void i18n.changeLanguage(defaultLanguage);
    }
  }, [defaultLanguage]);
  // chekin-sdk =-------------------------------------------------------------------

  const url = new URL(window.location.href);
  const pathSegments = url.pathname.split('/').filter(Boolean);
  const tokenFromUrl = pathSegments[0] || '';
  const shortToken = tokenFromUrl !== 'oauth' ? tokenFromUrl : '';
  const chekinSdk = React.useMemo(
    () =>
      new ChekinAPI({
        // For the SDK mode
        apikey: IS_SDK_MODE ? chekinProApiKey : '',
        resourceId: ChekinProSettings.reservationId || ChekinProSettings.housingId,
        externalResourceId:
          ChekinProSettings.externalReservationId || ChekinProSettings.externalHousingId,
        resourceType: getResourceType(),
        // For the Main mode
        isChekinConsumer: !IS_SDK_MODE,
        encryptionEnabled: IS_SDK_MODE,
      }),
    [chekinProApiKey],
  );

  const {initChekin, init} = chekinSdk.auth;

  const {
    data,
    error,
    isLoading: isAuthInitialization,
    isLoadingError: isAuthInitializationError,
    isSuccess: isSuccessSDK,
  } = useQuery<(OldShortLink | ShortLink) | SDKApiKeyResponse, Error>(
    [QUERY_CACHE_KEYS.apiClient],
    () => (IS_SDK_MODE ? init() : initChekin(shortToken)),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: IS_SDK_MODE ? Boolean(chekinProApiKey) : Boolean(shortToken),
      refetchOnReconnect: false,
    },
  );

  const isTokenValid = Boolean(data);
  const templateId = getTemplateId(data);

  React.useEffect(() => {
    if (isShortLink(data)) {
      let reservationId;
      if (isNewShortLink(data)) {
        reservationId = data?.instance_id;
      } else {
        const isReservationLink = data?.model_name === AUTH_INIT_MODELS.reservation;
        reservationId = isReservationLink ? data?.object_id : undefined;
      }

      setFoundReservationId(reservationId);
      LocalStorage.set(RESERVATION_ID_KEY, reservationId);
    }
  }, [data]);

  const linkType = isShortLink(data) ? data?.type : undefined;
  useRedirectToUrlFromShortLink({linkType, token: shortToken});

  const authError = React.useMemo<string | undefined>(() => {
    if (!isAuthInitializationError) return;
    if (IS_SDK_MODE) {
      if (!error) {
        return t('dns_is_forbidden_error_message');
      }
      return error?.message;
    }
  }, [error, isAuthInitializationError, t]);

  const mainToken = React.useMemo(() => {
    if (IS_SDK_MODE) {
      LocalStorage.set(SHARE_LINK, window.location.href);
    } else if (shortToken) {
      LocalStorage.set(MAIN_TOKEN_ID_KEY, shortToken);
      LocalStorage.set(
        SHARE_LINK,
        `${import.meta.env.VITE_APP_PUBLIC_URL}/${shortToken}`,
      );
    }

    return shortToken || LocalStorage.get(MAIN_TOKEN_ID_KEY);
  }, [shortToken]);

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      ...chekinSdk.verification,
      ...chekinSdk.identityVerification,
      ...chekinSdk.locations,
      ...chekinSdk.guest,
      ...chekinSdk.reservation,
      ...chekinSdk.taxes,
      ...chekinSdk.guestFormAdditionalData,
      ...chekinSdk.guestGroup,
      ...chekinSdk.customForm,
      ...chekinSdk.housing,
      ...chekinSdk.validations,
      ...chekinSdk.documents,
      ...chekinSdk.payments,
      ...chekinSdk.websocket,
      ...chekinSdk.upselling,
      ...chekinSdk.propertyProtection,
      ...chekinSdk.locks,
      ...chekinSdk.theme,
      ...chekinSdk.guidebooks,
      ...chekinSdk.auth,
      ...chekinSdk.feedback,
      isTokenValid,
      isAuthInitialization,
      isAuthInitializationError,
      authError,
      foundReservationId,
      mainToken,
      isSuccessSDK,
      objectId: getObjectDetails(data).id,
      modelName: getObjectDetails(data).model,
      link: getObjectDetails(data).link,
      redirectTo: getObjectDetails(data).redirectTo,
      reservationSearchMode: (getObjectDetails(data).searchMode ||
        RESERVATION_SEARCH_MODE.both) as RESERVATION_SEARCH_MODE,
      linkType,
      hasToken: Boolean(shortToken),
      templateId,
      customFormsVersion: getCustomFormVersion(data),
    }),
    [
      linkType,
      authError,
      chekinSdk,
      isAuthInitialization,
      isAuthInitializationError,
      isSuccessSDK,
      isTokenValid,
      mainToken,
      foundReservationId,
      data,
      shortToken,
      templateId,
    ],
  );

  return <ChekinSDKContext.Provider value={defaultProps} children={children} />;
}

export {ChekinSDKContext, ChekinSDKProvider};
